/**
 * Reading a crew suggestion off the wire (RUT-3316).
 *
 * `GET /crews?recommendFor…` answers for several customers in one response, so
 * every crew carries a `recommendations` list rather than a single verdict. Both
 * surfaces that show a suggestion — the activation dialog and the Services →
 * Configuration table — narrow that to one customer through here, so neither
 * grows its own idea of what "the suggested crew" means.
 *
 * Nothing here decides anything. The ranking is worked out server-side by
 * `rankCrewsByNearbyCustomers` in `@ruta/shared`; this only picks one customer's
 * slice out of it.
 */
import type { CrewCustomerRecommendation, CrewRecommendationSummary } from '@ruta/shared';

/** The wire shape this reads — any crew that may carry suggestions. */
export interface CrewWithRecommendations {
  id: string;
  recommendations?: CrewCustomerRecommendation[];
}

/**
 * Key for one row's suggestions. A row is a customer AND a service — the same
 * customer can be awaiting configuration on two services at once, and each has
 * its own neighbourhood.
 */
export function crewSuggestionKey(customerId: string, serviceTemplateId: string): string {
  return `${customerId}|${serviceTemplateId}`;
}

/** One customer's suggestions, keyed by crew id. Empty when nothing fits. */
export type CrewSuggestionMap = Map<string, CrewRecommendationSummary>;

export const NO_CREW_SUGGESTIONS: CrewSuggestionMap = new Map();

/**
 * Narrow a crew list to what it says about one customer.
 *
 * An empty map is a real answer — no crew is a reasonable fit, so nothing should
 * be put forward and the admin chooses manually.
 */
export function crewSuggestionsFor(
  crews: CrewWithRecommendations[] | undefined,
  customerId: string | undefined,
): CrewSuggestionMap {
  if (!crews?.length || !customerId) return NO_CREW_SUGGESTIONS;
  const out: CrewSuggestionMap = new Map();
  for (const crew of crews) {
    const match = crew.recommendations?.find((r) => r.customerId === customerId);
    if (match) {
      out.set(crew.id, {
        rank: match.rank,
        nearbyCustomerCount: match.nearbyCustomerCount,
        nearbyTotalCount: match.nearbyTotalCount,
        distanceMeters: match.distanceMeters,
      });
    }
  }
  return out;
}

/** The crew put forward as the best fit, or null when none is. */
export function topSuggestedCrewId(suggestions: CrewSuggestionMap): string | null {
  for (const [crewId, rec] of suggestions) if (rec.rank === 1) return crewId;
  return null;
}

/**
 * More than one crew already works this neighbourhood on this service.
 * The closed cell then says so, instead of naming one crew as Suggested
 * (RUT-3964) — the ranking itself does not change.
 */
export function hasMultipleSuggestions(suggestions: CrewSuggestionMap): boolean {
  return suggestions.size > 1;
}

/**
 * Why a crew is suggested, in words an admin can act on — "does this service for
 * 8 of the 11 customers nearby" rather than a score.
 *
 * The two numbers are the crew's share of one neighbourhood of customers who
 * already have this service, which is the only thing the suggestion is derived
 * from. Saying both keeps "8 nearby" from reading as though the crew owned the
 * whole area.
 */
export function nearbyLabel(rec: CrewRecommendationSummary | undefined): string {
  if (!rec) return '';
  const { nearbyCustomerCount: mine, nearbyTotalCount: total } = rec;
  const noun = total === 1 ? 'customer' : 'customers';
  return mine === total
    ? `all ${total} nearby ${noun} on this service`
    : `${mine} of ${total} nearby ${noun} on this service`;
}

/**
 * The invitation to override, worded once so the two surfaces that show a
 * suggestion cannot drift into saying it differently.
 */
export const CHANGE_SUGGESTION_HINT = "Change it to any crew if that's not right.";

/** Badge on the single best-fit crew — closed cell (one neighbour crew) and open list. */
export const SUGGESTED_BADGE = 'Suggested';

/**
 * Closed-cell label when more than one crew already works nearby. Implies
 * "open this — you will see more than the top pick" (RUT-3964).
 */
export const MULTIPLE_CREWS_SUGGESTED_LABEL = 'Multiple crews suggested';

/**
 * The sentence above the crew list — names the best fit and why. When more
 * than one crew already works nearby, that comes first so the admin knows
 * to look at the rest of the list (RUT-3964).
 */
export function suggestionIntro(
  crewName: string,
  rec: CrewRecommendationSummary | undefined,
  options?: { multiple?: boolean },
): string {
  const reason = `${crewName} already does ${nearbyLabel(rec)}. ${CHANGE_SUGGESTION_HINT}`;
  return options?.multiple
    ? `More than one crew already works nearby. ${reason}`
    : `Suggested — ${reason}`;
}

/**
 * Order crews so suggested ones come first, best match first, and everything
 * else keeps the order it arrived in. Used by both surfaces so the list an admin
 * reads is the same shape wherever they meet it.
 */
export function orderBySuggestion<C extends { id: string }>(
  crews: C[],
  suggestions: CrewSuggestionMap,
  tieBreak?: (a: C, b: C) => number,
): C[] {
  return [...crews].sort((a, b) => {
    const ar = suggestions.get(a.id)?.rank ?? Number.MAX_SAFE_INTEGER;
    const br = suggestions.get(b.id)?.rank ?? Number.MAX_SAFE_INTEGER;
    if (ar !== br) return ar - br;
    return tieBreak ? tieBreak(a, b) : 0;
  });
}
